/* eslint quotes: 0, no-use-before-define: 0, quote-props: 0 */
import Logger from './logger';

// utility functions for taking our objects and returning schemas to be written to the page
// these schema are used for indexing so our items eg events are discoverable using serach engines
const OFFLINE_EVENT_ATTENDANCE_MODE = "https://schema.org/OfflineEventAttendanceMode";
const EVENT_SCHEDULED = "https://schema.org/EventScheduled";
const EVENT = "Event";
const IN_STOCK = "http://schema.org/InStock";
const OFFER = "Offer";
const POSTAL_ADDRESS = "PostalAddress";
const RESTAURANT = "Restaurant";
const SOLD_OUT = "http://schema.org/SoldOut";

export default function schema(obj, type = EVENT) {
  const base = {
    "@context": "https://schema.org",
    "@type": type,
  };

  try {
    switch (type) {
      case EVENT:
        return { ...base, ...event(obj) };

      case RESTAURANT:
        return { ...base, ...restaurant(obj) };

      default:
        return base;
    }
  } catch (e) {
    Logger.error('creating the schema failed');
  }

  return base;
}

export function normalizeLocation(venue = {}) {
  return {
    country: venue?.country || venue?.location?.country,
    locality: venue?.locality || venue?.location?.city,
    region: venue?.region || venue?.location?.state,
    postalCode: venue?.postal_code || venue?.location?.postalCode,
    address1: venue?.address_1 || venue?.location?.address1,
    address2: venue?.address_2 || venue?.location?.address2,
  };
}

export function address(venue, type = POSTAL_ADDRESS) {
  const normalized = normalizeLocation(venue);
  return {
    "@type": type,
    "addressCountry": normalized.country,
    "addressLocality": normalized.locality,
    "addressRegion": normalized.region,
    "postalCode": normalized.postalCode,
    "streetAddress": getStreetAddress(normalized.address1, normalized.address2),
  };
}

export function event(obj) {
  return {
    "about": { "description": obj.description },
    "description": obj.description,
    "endDate": `${obj.date}T${obj.end_time}`,
    "eventAttendanceMode": OFFLINE_EVENT_ATTENDANCE_MODE,
    "eventStatus": EVENT_SCHEDULED,
    "image": obj.image_preview,
    "location": location(obj),
    "offers": offers(obj),
    "name": obj.name,
    "startDate": `${obj.date}T${obj.time}`,
  };
}

export function aggregateRating(venue) {
  const {
    rater: [
      { score, total } = {},
    ] = [],
  } = venue;

  if (!total) {
    return undefined;
  }

  return {
    '@type': 'AggregateRating',
    'bestRating': 5,
    'ratingCount': total,
    'ratingValue': getRatingValue(score),
  };
}

export function geo(venue) {
  const {
    location: {
      coords: {
        lat, long,
      } = {},
    } = {},
  } = venue;

  if (lat && long) {
    return {
      '@type': 'GeoCoordinates',
      'latitude': lat,
      'longitude': long,
    };
  }
  return undefined;
}

export function restaurant(venue) {
  const {
    contact: {
      phone: {
        phoneUrl: telephone,
      } = {},
    } = {},
    cuisine: servesCuisine,
    name,
    priceRange,
  } = venue;

  if (!name) {
    return undefined;
  }

  const about = venue.content?.find((item) => item.name === 'about');

  return {
    name,
    address: address(venue),
    description: about?.body,
    geo: geo(venue),
    image: image(venue),
    potentialAction: reserveAction(),
    priceRange: priceRange && '$'.repeat(priceRange),
    servesCuisine,
    telephone,
  };
}

export function image(venue) {
  if (Array.isArray(venue.images) && venue.images.length > 0) {
    return venue.images[0].url;
  }
  return undefined;
}

export function reserveAction() {
  return {
    '@type': 'ReserveAction',
    'target': {
      '@type': 'EntryPoint',
      'urlTemplate': `${location.protocol}//${location.host}${location.pathname}`,
      'inLanguage': 'en-US',
      'actionPlatform': [
        'http://schema.org/MobileWebPlatform',
        'http://schema.org/DesktopWebPlatform',
        'http://schema.org/IOSPlatform',
        'http://schema.org/AndroidPlatform',
      ],
    },
    'result': {
      '@type': 'FoodEstablishmentReservation',
      'name': 'Reserve now',
    },
  };
}

export function review(venue) {
  // eslint-disable-next-line no-unused-vars
  const { rater: [rating0, rating1] } = venue;

  if (rating1 && rating1.name !== 'Resy') {
    const {
      name, scale, score,
    } = rating1;

    return {
      '@type': 'Review',
      'author': {
        '@type': 'Organization',
        'name': name,
      },
      'reviewRating': {
        'bestRating': scale,
        'ratingValue': score,
      },
    };
  }

  return undefined;
}

export function location(obj, type = RESTAURANT) {
  return {
    '@type': type,
    'address': address(obj.venue),
    'name': obj.venue?.name,
    'telephone': obj.venue?.phone_number,
  };
}

export function offers(obj) {
  const { currency } = obj.price_summary;
  const offersList = obj.packages?.map((item) => offer(item, currency));
  return (offersList?.length === 1) ? offersList[0] : offersList;
}

export function offer(
  obj,
  currency = 'USD',
  type = OFFER
) {
  return {
    "@type": type,
    "availability": obj.sold_out ? SOLD_OUT : IN_STOCK,
    "priceCurrency": currency,
    "name": obj.name,
    "price": obj.payment?.base?.toString(),
    "url": window.location.href,
  };
}

// HELPERS
export function getStreetAddress(address1, address2) {
  return `${address1 || ''} ${address2 || ''}`.trim();
}

// examples: 1.3 -> 1.5, 2.2 -> 2, 3.9 -> 4
export function getRatingValue(rating) {
  if (typeof rating === 'number') {
    return Math.round(rating * 2) / 2;
  }
  return undefined;
}
